import { FireIcon, SparklesIcon } from '@heroicons/react/24/solid';
import clsx from 'clsx';
import { EstimatedLoadCardProps } from 'components/structure-page/estimated-load-card-types';

export default function EstimatedLoadCard({
  status,
  totals,
  pendingMessage,
  emptyMessage,
  footnote
}: EstimatedLoadCardProps) {
  const hasTotals = totals.heating > 0 || totals.cooling > 0;
  const recalculating = status === 'loading';

  return (
    <div className='relative w-full max-w-xl rounded-md border border-gray-300 bg-white p-5 shadow-sm'>
      <div
        aria-busy={recalculating}
        className={clsx(
          'flex flex-col gap-4 transition-opacity duration-150',
          recalculating && 'pointer-events-none opacity-40'
        )}
      >
        <strong className='text-lg font-semibold text-gray-800'>
          Estimated cooling and heating needs
        </strong>

        {hasTotals ? (
          <div className='grid grid-cols-2 gap-4'>
            <div className='flex flex-col gap-1 rounded-md border border-gray-200 bg-gray-50 px-4 py-3'>
              <span className='flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500'>
                <SparklesIcon className='h-4 w-4 text-sky-500' />
                Cooling
              </span>
              <strong
                aria-live='polite'
                className='text-2xl tabular-nums tracking-tight text-gray-900'
              >
                {totals.cooling.toLocaleString('en-US')}
              </strong>
              <span className='text-sm text-gray-500'>BTUs per hour</span>
            </div>
            <div className='flex flex-col gap-1 rounded-md border border-gray-200 bg-gray-50 px-4 py-3'>
              <span className='flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500'>
                <FireIcon className='h-4 w-4 text-orange-500' />
                Heating
              </span>
              <strong
                aria-live='polite'
                className='text-2xl tabular-nums tracking-tight text-gray-900'
              >
                {totals.heating.toLocaleString('en-US')}
              </strong>
              <span className='text-sm text-gray-500'>BTUs per hour</span>
            </div>
          </div>
        ) : (
          <p className='text-sm leading-relaxed text-gray-600'>
            {recalculating ? pendingMessage : emptyMessage}
          </p>
        )}

        <p className='text-sm leading-relaxed text-gray-500'>{footnote}</p>
      </div>

      {recalculating && (
        <div className='absolute inset-0 flex items-center justify-center'>
          <svg
            className='h-6 w-6 animate-spin text-gray-500'
            xmlns='http://www.w3.org/2000/svg'
            fill='none'
            viewBox='0 0 24 24'
            aria-label='Recalculating'
          >
            <circle
              className='opacity-25'
              cx='12'
              cy='12'
              r='10'
              stroke='currentColor'
              strokeWidth='4'
            />
            <path
              className='opacity-75'
              fill='currentColor'
              d='M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z'
            />
          </svg>
        </div>
      )}
    </div>
  );
}
